import { faVideoSlash } from '@fortawesome/pro-solid-svg-icons';
import { useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

import { useTranslate } from '@growtherapy/i18n';
import {
  AccessibleSelect,
  Checkbox,
  IconBadge,
  InputControl,
  MenuDivider,
  SelectOptionAtom,
  cn,
} from '@growtherapy/sprout-ui';

import { useTheme } from '../../hooks';
import { useVideoPlatform } from '../../hooks/useVideoPlatform';
import { LocalAudioVideoThumbnailProps, QualityPersistanceType, useVideoQualityCallbacks } from '../../session';
import { NoAccessVariant } from '../../session/controls';
import { getDeviceOptionsFromDeviceList, useUpdateAvailableDevicesInterval } from '../../session/devices/useDevices';
import { Theme } from '../../state';
import { toast } from '../../toasts';
import {
  ChangeCameraStatusCode,
  LocalMediaStatus,
  QualityMode,
  VideoPlatform,
  currentCameraIdAtom,
  localVideoStatusAtom,
  useStoredAudioVideoSettings,
  useSwitchCamera,
} from '../../video-sdk';
import {
  availableDevicesAtom,
  canOverrideSuggestedRoomVideoQualityAtom,
  permissionDeniedAtom,
  shouldOverrideSuggestedRoomVideoQualityAtom,
  shouldSuggestPerformanceModeAtom,
  videoQualityAtom,
} from '../../video-sdk/twilio';
import { BlockedPermissionMessageWrapper as BlockedPermissionMessage } from './BlockedPermissionMessage';
import { VideoQualitySettings, VideoQualitySettingsProps } from './VideoQualityOptions';

interface VideoSettingsTabProps extends VideoQualitySettingsProps {
  cameraOptions: SelectOptionAtom[];
  setCamera: (selectedId: string) => void;
  selectedCamera?: string;
  thumbnailComponent: React.FC<Omit<LocalAudioVideoThumbnailProps, 'name'>>;
  videoIsBlocked: boolean;
  videoIsLoading?: boolean;
  isMirrored: boolean;
  setIsMirrored: (isMirrored: boolean) => void;
  /**
   * Whether to show the video quality settings section. This section was built
   * out to support Twilio Video quality optimizations and should be hidden when
   * Twilio Video is not in use.
   */
  shouldShowVideoQualitySettings?: boolean;
}

export function VideoSettingsTab({
  cameraOptions,
  setCamera,
  selectedCamera,
  thumbnailComponent: LocalAudioVideoThumbnail,
  videoIsBlocked,
  videoIsLoading,
  isMirrored,
  setIsMirrored,
  qualityMode,
  onChangeQualityMode,
  shouldShowAutoOptimizationAlert,
  storedQualityMode,
  shouldShowVideoQualitySettings,
}: VideoSettingsTabProps) {
  const t = useTranslate();
  const { theme } = useTheme();

  const isDarkTheme = theme === Theme.DARK;
  const shouldDisableFields = videoIsBlocked || videoIsLoading;

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex justify-center'>
        <LocalAudioVideoThumbnail
          videoClassName='max-h-none max-w-none w-full h-[216px]'
          className='w-full h-full'
          showExpandVideoToggle={false}
          isSettingsThumbnail={true}
          isSpeaking={false}
          shouldShowHideSelf={false}
          videoOffContent={
            <IconBadge size='xl' className='bg-neutral-800' iconClassName='text-coral-600' icon={faVideoSlash} />
          }
        />
      </div>
      <InputControl
        label={t('telehealth-core:settings.camera')}
        disabled={shouldDisableFields}
        className='mb-0'
        labelClassName={cn({
          'rebrand:text-text-inverse': isDarkTheme,
        })}
      >
        <AccessibleSelect
          onChange={(value: string) => setCamera(value)}
          options={cameraOptions}
          value={selectedCamera ?? cameraOptions[0]?.value}
          placeholder={videoIsBlocked ? t('telehealth-core:settings.noAccess') : t('telehealth-core:settings.select')}
        />
      </InputControl>
      <Checkbox
        label={t('telehealth-core:settings.mirrorMyVideo')}
        checked={isMirrored}
        onChange={() => setIsMirrored(!isMirrored)}
        disabled={shouldDisableFields}
        iconClassName={cn({
          'rebrand:text-text-inverse': isDarkTheme,
        })}
        textClassName={cn({
          'rebrand:text-text-inverse': isDarkTheme,
        })}
      />
      {videoIsBlocked && <BlockedPermissionMessage noAccessVariant={NoAccessVariant.Camera} />}
      {shouldShowVideoQualitySettings && (
        <>
          <MenuDivider />
          <VideoQualitySettings
            qualityMode={qualityMode}
            onChangeQualityMode={onChangeQualityMode}
            shouldDisableFields={shouldDisableFields}
            shouldShowAutoOptimizationAlert={shouldShowAutoOptimizationAlert}
            storedQualityMode={storedQualityMode}
          />
        </>
      )}
    </div>
  );
}

export function VideoSettingsTabWrapper(props: Pick<VideoSettingsTabProps, 'thumbnailComponent'>) {
  const t = useTranslate();
  useUpdateAvailableDevicesInterval();

  const devices = useAtomValue(availableDevicesAtom);
  const shouldShowAutoOptimizationAlert = useAtomValue(shouldSuggestPerformanceModeAtom);
  const { videoIsBlocked } = useAtomValue(permissionDeniedAtom);
  const localVideoStatus = useAtomValue(localVideoStatusAtom);
  const videoIsLoading = !localVideoStatus || localVideoStatus === LocalMediaStatus.LOADING;
  const { storedAudioVideoSettings, setStoredAudioVideoSettings } = useStoredAudioVideoSettings();
  const onToggleMirroredVideo = (isMirrored: boolean) => {
    setStoredAudioVideoSettings((settings) => ({
      ...settings,
      videoIsMirrored: isMirrored,
    }));
  };
  const cameraOptions = getDeviceOptionsFromDeviceList(devices?.videoInputDevices);
  const changeCameraDevice = useSwitchCamera();
  const videoQuality = useAtomValue(videoQualityAtom);
  const getCanOverrideSuggestedRoomVideoQuality = useAtomCallback(
    useCallback((get) => get(canOverrideSuggestedRoomVideoQualityAtom), []),
  );
  const setShouldOverrideSuggestedQualityMode = useSetAtom(shouldOverrideSuggestedRoomVideoQualityAtom);
  const { changeVideoQuality } = useVideoQualityCallbacks();
  const videoPlatformIsZoom = useVideoPlatform() === VideoPlatform.Zoom;

  // This debounce shouldn't be strictly necessary, but it seems like the select
  // component can call its onChange twice on Android devices which causes
  // unexpected behavior
  const setCamera = useDebounceCallback(
    useCallback(
      async (deviceId: string) => {
        const { success, code } = await changeCameraDevice(deviceId);
        if (success) return;
        switch (code) {
          case ChangeCameraStatusCode.ERROR:
            toast.error(t('telehealth-core:settings.unableToChangeCameraDevice'));
            return;
          case ChangeCameraStatusCode.BACKGROUND_ERROR:
            toast.error(t('telehealth-core:settings.unableToSetBackgroundAfterChangingCameraDevice'));
            return;
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),
    100,
  );
  const onChangeVideoQualityMode = useDebounceCallback(
    useCallback(
      async (qualityMode: QualityMode) => {
        const { status } = await changeVideoQuality(qualityMode, {
          persistanceType: QualityPersistanceType.STORAGE_AND_MEMORY,
        });
        if (status === 'success') {
          if (getCanOverrideSuggestedRoomVideoQuality()) {
            setShouldOverrideSuggestedQualityMode(true);
          }
          return;
        }
        toast.error(t('telehealth-core:settings.unableToChangeYourVideoQualityDueToAnIssue'));
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),
    200,
  );
  const selectedCamera = useAtomValue(currentCameraIdAtom);
  const correspondingCameraId = cameraOptions.some((option) => option.value === selectedCamera)
    ? selectedCamera
    : storedAudioVideoSettings?.inputVideoDeviceId;
  return (
    <VideoSettingsTab
      cameraOptions={cameraOptions}
      setCamera={setCamera}
      selectedCamera={correspondingCameraId}
      videoIsBlocked={videoIsBlocked}
      videoIsLoading={videoIsLoading}
      isMirrored={storedAudioVideoSettings?.videoIsMirrored ?? false}
      qualityMode={videoQuality}
      setIsMirrored={onToggleMirroredVideo}
      onChangeQualityMode={onChangeVideoQualityMode}
      shouldShowAutoOptimizationAlert={shouldShowAutoOptimizationAlert}
      storedQualityMode={storedAudioVideoSettings?.videoQualityMode}
      shouldShowVideoQualitySettings={!videoPlatformIsZoom}
      {...props}
    />
  );
}
